import { MembershipType } from '../types/member';
import { PetitionType, type MemberPetition } from '../types/petition';
import { tryNormalizeEnumValue } from './enumNormalize';
import { normalizePetitionType } from './petition';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export interface MembershipChangePayload {
  targetMembershipType: MembershipType | null;
  reason: string;
  isStructured: boolean;
}

export function parseMembershipChangePayload(
  payload: string | undefined | null
): MembershipChangePayload {
  if (!payload?.trim()) {
    return { targetMembershipType: null, reason: '', isStructured: false };
  }
  const trimmed = payload.trim();
  try {
    const parsed = JSON.parse(trimmed) as {
      targetMembershipType?: unknown;
      TargetMembershipType?: unknown;
      reason?: string;
      Reason?: string;
    };
    if (parsed && typeof parsed === 'object') {
      const rawTarget = parsed.targetMembershipType ?? parsed.TargetMembershipType;
      const target = tryNormalizeEnumValue(MembershipType, rawTarget);
      return {
        targetMembershipType: target ?? null,
        reason: (parsed.reason ?? parsed.Reason ?? '').trim(),
        isStructured: true,
      };
    }
  } catch {
    // legacy plain text
  }
  return { targetMembershipType: null, reason: trimmed, isStructured: false };
}

export function membershipChangeIsApprovable(petition: MemberPetition): boolean {
  if (normalizePetitionType(petition.petitionType) !== PetitionType.ChangeMembershipType) {
    return true;
  }
  const fromDto = tryNormalizeEnumValue(MembershipType, petition.targetMembershipType);
  if (fromDto != null) return true;
  const parsed = parseMembershipChangePayload(petition.payload);
  return parsed.targetMembershipType != null;
}

export function membershipTypeLabel(value: unknown, t: Translate): string {
  const normalized = tryNormalizeEnumValue(MembershipType, value);
  if (normalized === MembershipType.Regular) return t('membershipType.regular');
  if (normalized === MembershipType.Student) return t('membershipType.student');
  if (normalized === MembershipType.Retired) return t('membershipType.retired');
  if (normalized === MembershipType.Honorary) {
    return t('membershipType.honorary', { defaultValue: 'Honorary' });
  }
  return '';
}

/** Human-readable petition payload (plain text or membership-change JSON). */
export function formatPetitionPayload(
  payload: string | undefined | null,
  t: Translate
): string {
  if (!payload?.trim()) return '';
  const trimmed = payload.trim();
  try {
    const parsed = JSON.parse(trimmed) as {
      targetMembershipType?: unknown;
      TargetMembershipType?: unknown;
      reason?: string;
      Reason?: string;
    };
    if (parsed && typeof parsed === 'object') {
      const target = parsed.targetMembershipType ?? parsed.TargetMembershipType;
      const reason = (parsed.reason ?? parsed.Reason ?? '').trim();
      const category = membershipTypeLabel(target, t);
      if (category || reason) {
        const parts: string[] = [];
        if (category) {
          parts.push(
            t('petitions.requestedCategory', {
              category,
              defaultValue: `Requested category: ${category}`,
            })
          );
        }
        if (reason) parts.push(reason);
        return parts.join(' — ');
      }
    }
  } catch {
    // plain text
  }
  return trimmed;
}
